import {
  Close,
  ContentCopy,
  EmailOutlined,
  NavigateNext,
  PhoneOutlined,
  WhatsApp,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import apiService from "../../../../../../service/Api";
import { RootState } from "../../../../../../store/Index";
import { tokens } from "../../../../../../theme";
import type { OrganizerCardProps } from "../../../types/types";

export interface BookingPreview {
  id: string | number;
  deceasedName: string;
  serviceType: string;
  date: string;
  avatar?: string;
}

interface RawBooking {
  id: number;
  service: string;
  deceased?: Array<{
    first_name?: string;
    surname?: string;
    person_image?: string | null;
  }>;
  services?: Array<{ date_of_service?: string }>;
  title?: string | null;
}

interface ContactDetailsDialogProps {
  open: boolean;
  client?: OrganizerCardProps;
  currentUserName?: string;
  onClose: () => void;
  onBookingClick?: (bookingId: string | number) => void;
  onViewAllBookings?: () => void;
  onWhatsAppClick?: () => void;
}

const ContactDetailsDialog: React.FC<ContactDetailsDialogProps> = ({
  open,
  client,
  currentUserName = "Muzukuru Staff",
  onClose,
  onBookingClick,
  onViewAllBookings,
  onWhatsAppClick,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const [bookings, setBookings] = useState<BookingPreview[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [fetchedForId, setFetchedForId] = useState<string | number | null>(
    null,
  );

  useEffect(() => {
    if (!client?.id || fetchedForId === client.id) return;
    let cancelled = false;

    const fetchBookings = async () => {
      setBookingsLoading(true);
      try {
        const res = await apiService.get<{ results: RawBooking[] }>(
          `/api/bookings/booking/?contact_id=${client.id}`,
        );
        if (cancelled) return;

        const mapped: BookingPreview[] = res.data.results.map((b) => {
          const deceased = b.deceased?.[0];
          const deceasedName =
            deceased?.first_name && deceased?.surname
              ? `${deceased.first_name} ${deceased.surname}`
              : b.title || "Unknown";
          return {
            id: b.id,
            deceasedName,
            serviceType: b.service || "Service",
            date: b.services?.[0]?.date_of_service
              ? new Date(b.services[0].date_of_service).toLocaleDateString(
                  "en-GB",
                )
              : "N/A",
            avatar: deceased?.person_image ?? undefined,
          };
        });

        setBookings(mapped);
        setFetchedForId(client.id);
      } catch {
        if (!cancelled) setBookings([]);
      } finally {
        if (!cancelled) setBookingsLoading(false);
      }
    };

    fetchBookings();
    return () => {
      cancelled = true;
    };
  }, [client?.id, fetchedForId]);

  const displayedBookings = bookings.slice(0, 3);

  const getInitials = (name?: string) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
  };

  const handleCall = () => {
    if (client?.phone) window.location.href = `tel:${client.phone}`;
  };

  const handleWhatsApp = () => {
    if (onWhatsAppClick) {
      onWhatsAppClick();
    } else {
      const phone = client?.phone?.replace(/\D/g, "") || "";
      const msg = `Hi ${client?.name || "Client"}, this is ${currentUserName} from Muzukuru Funeral Streaming.`;
      window.open(
        `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`,
        "_blank",
      );
    }
  };

  const handleSendEmail = () => {
    if (client?.email) window.location.href = `mailto:${client.email}`;
  };

  const handleCopyPhone = () => {
    if (client?.phone) navigator.clipboard.writeText(client.phone);
  };

  const handleCopyEmail = () => {
    if (client?.email) navigator.clipboard.writeText(client.email);
  };

  const handleViewContact = () => {
    if (!client?.id || !client?.contact_type) return;
    window.open(`/contacts/${client.contact_type}/view/${client.id}`, "_blank");
  };

  const isDark = theme.palette.mode === "dark";
  const textMuted = colors.primary[300];
  const accent = colors.primary[400];
  const accentLight = colors.primary[500];
  const borderColor = isDark
    ? colors.primary[300] + "25"
    : colors.primary[300] + "40";
  const pillBg = isDark ? colors.primary[800] : colors.primary[900];
  const hoverBg = isDark ? colors.primary[300] + "12" : colors.primary[800];
  const dividerColor = isDark
    ? colors.primary[300] + "20"
    : colors.primary[300] + "25";

  const PillRow = ({
    icon,
    iconBg,
    label,
    value,
    actions,
  }: {
    icon: React.ReactNode;
    iconBg: string;
    label: string;
    value: string;
    actions: React.ReactNode;
  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1.5px solid",
        borderColor,
        borderRadius: "50px",
        bgcolor: pillBg,
        px: 1.5,
        py: 1,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          bgcolor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: textMuted,
            textTransform: "uppercase",
            fontSize: "9px",
            letterSpacing: 0.5,
            display: "block",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: "13px",
            color: colors.primary[700],
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography>
      </Box>

      <Stack direction="row" spacing={0.5} alignItems="center">
        {actions}
      </Stack>
    </Box>
  );

  const ActionIcon = ({
    onClick,
    children,
    color,
    bgColor,
  }: {
    onClick?: () => void;
    children: React.ReactNode;
    color?: string;
    bgColor?: string;
  }) => (
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        width: 30,
        height: 30,
        color: color ?? textMuted,
        bgcolor: bgColor ?? "transparent",
        "&:hover": { bgcolor: bgColor ? bgColor + "cc" : hoverBg },
      }}
    >
      {children}
    </IconButton>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          bgcolor: colors.primary[900],
          boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        },
      }}
    >
      <DialogContent sx={{ p: 3, pb: 1, }}>
        {/* <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: textMuted,
              "&:hover": { bgcolor: dividerColor },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box> */}

        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
          <Avatar
            src={client?.contact_image || undefined}
            sx={{
              width: 72,
              height: 72,
              bgcolor: accent,
              fontSize: "22px",
              fontWeight: 700,
              border: "1px solid",
              borderColor: isDark ? colors.primary[300] + "30" : accentLight,
            }}
          >
            {!client?.contact_image && getInitials(client?.name)}
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              lineHeight={1.2}
              sx={{ color: colors.primary[700] }}
            >
              {client?.name || "N/A"}
            </Typography>
            {client?.relation && (
              <Typography
                variant="body2"
                sx={{ color: colors.primary[100], mt: 0.3 }}
              >
                {client.relation}
              </Typography>
            )}
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              alignSelf: "flex-start",
              bgcolor: hoverBg,
              color: textMuted,
              "&:hover": { bgcolor: dividerColor },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        {/* Email pill */}
        <Box mb={1.5}>
          <PillRow
            icon={<EmailOutlined sx={{ fontSize: 18, color: "#ffffff" }} />}
            iconBg={colors.primary[400]}
            label="Email"
            value={client?.email || "—"}
            actions={
              <>
                <ActionIcon
                  onClick={handleSendEmail}
                  color={colors.primary[400]}
                  bgColor={colors.primary[500] + "10"}
                >
                  <EmailOutlined sx={{ fontSize: 16 }} />
                </ActionIcon>
                <ActionIcon
                  onClick={handleCopyEmail}
                  bgColor={colors.primary[800]}
                >
                  <ContentCopy sx={{ fontSize: 15 }} />
                </ActionIcon>
              </>
            }
          />
        </Box>

        {/* Phone pill */}
        <Box mb={2.5}>
          <PillRow
            icon={<WhatsApp sx={{ fontSize: 18, color: "#ffffff" }} />}
            iconBg={colors.primary[400]}
            label="Mobile"
            value={client?.phone || "No phone number"}
            actions={
              <>
                <ActionIcon
                  onClick={handleCall}
                  color="#1976D2"
                  bgColor="#1976D215"
                >
                  <PhoneOutlined sx={{ fontSize: 16 }} />
                </ActionIcon>
                <ActionIcon
                  onClick={handleWhatsApp}
                  color="#25D366"
                  bgColor="#25D36615"
                >
                  <WhatsApp sx={{ fontSize: 16 }} />
                </ActionIcon>
                {/* <ActionIcon onClick={handleSms} color="#FF9800">
                  <Sms sx={{ fontSize: 15 }} />
                </ActionIcon> */}
                <ActionIcon
                  onClick={handleCopyPhone}
                  bgColor={colors.primary[800]}
                >
                  <ContentCopy sx={{ fontSize: 15 }} />
                </ActionIcon>
              </>
            }
          />
        </Box>

        {/* Past Bookings */}
        <Typography
          variant="subtitle2"
          fontWeight={700}
          mb={1.5}
          color={colors.primary[700]}
        >
          Past Bookings
        </Typography>

        <Box
          sx={{
            bgcolor: colors.primary[800],
            borderRadius: "14px",
            overflow: "hidden",
            border: "1px solid",
            borderColor,
          }}
        >
          {bookingsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} sx={{ color: accent }} />
            </Box>
          ) : displayedBookings.length > 0 ? (
            <>
              {displayedBookings.map((booking, idx) => (
                <Box key={booking.id}>
                  <Box
                    onClick={() => onBookingClick?.(booking.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      py: 1.5,
                      cursor: "pointer",
                      transition: "background 0.15s",
                      "&:hover": { bgcolor: hoverBg },
                    }}
                  >
                    <Avatar
                      src={booking.avatar}
                      sx={{
                        width: 40,
                        height: 40,
                        mr: 1.5,
                        fontSize: "13px",
                        bgcolor: accent,
                      }}
                    >
                      {getInitials(booking.deceasedName)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        fontSize="13px"
                        color={colors.primary[700]}
                      >
                        {booking.deceasedName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.primary[100] }}
                      >
                        {booking.serviceType} · {booking.date}
                      </Typography>
                    </Box>
                    <NavigateNext
                      sx={{ color: colors.primary[500], fontSize: 20 }}
                    />
                  </Box>

                  {idx < displayedBookings.length - 1 && (
                    <Divider sx={{ mx: 2, borderColor: dividerColor }} />
                  )}
                </Box>
              ))}

              <Divider sx={{ borderColor: dividerColor }} />
              <Box
                onClick={onViewAllBookings}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  py: 1.2,
                  cursor: "pointer",
                  gap: 0.5,
                  "&:hover": { bgcolor: hoverBg },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: accent }}
                >
                  View All Bookings
                </Typography>
                <NavigateNext sx={{ color: accent, fontSize: 16 }} />
              </Box>
            </>
          ) : (
            <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: textMuted }}>
                No bookings found
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleViewContact}
          disabled={!client?.id || !client?.contact_type}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            bgcolor: accent,
            color: colors.primary[900],
            "&:hover": { bgcolor: accentLight },
          }}
        >
          View Contact
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            borderColor: colors.primary[300],
            color: colors.primary[700],
            "&:hover": { borderColor: accent, bgcolor: hoverBg },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDetailsDialog;
